/**
 * Field utility functions that work with backend-fetched configuration.
 * Replaces the deprecated hardcoded functions from constants/fields.ts
 */

import { FieldsConfig, FieldDefinition, FieldOption } from '../services/configService';
import { SiteMetadata } from '../services/siteService';
import { FIELD_VALUE_EXTRACTING } from '../constants';

export type FieldValue = string | string[] | Record<string, string[]>;

export function isGroupedFieldValue(value: FieldValue | null | undefined): value is Record<string, string[]> {
  return !!value && typeof value === 'object' && !Array.isArray(value);
}

export function getMeaningfulGroupEntries(
  value: Record<string, string[]>
): [string, string[]][] {
  return Object.entries(value).filter(([, vals]) => {
    if (!vals) return false;
    const arr = Array.isArray(vals) ? vals : [vals];
    return arr.some(v => v !== '<UNKNOWN>');
  });
}

/** Human-readable one-line rendering of a field value (grouped / list / scalar). */
export function formatFieldValueOneLine(value: FieldValue | null | undefined): string {
  if (value === undefined || value === null) return '—';
  if (isGroupedFieldValue(value)) {
    const parts = getMeaningfulGroupEntries(value).map(([key, vals]) => {
      const arr = Array.isArray(vals) ? vals : [vals];
      return `${key}: ${arr.filter((v) => v !== '<UNKNOWN>').join(', ')}`;
    });
    return parts.length ? parts.join('; ') : '—';
  }
  if (Array.isArray(value)) {
    const kept = value.filter((v) => v !== '<UNKNOWN>');
    return kept.length ? kept.join(', ') : '—';
  }
  return value === '<UNKNOWN>' ? '—' : String(value);
}

export function isFieldPending(value: FieldValue | null | undefined): boolean {
  if (!value) return true;
  if (isGroupedFieldValue(value)) return Object.keys(value).length === 0;
  if (Array.isArray(value)) {
    return value.length === 0 || (value.length === 1 && value[0] === '<UNKNOWN>');
  }
  if (value === '<UNKNOWN>') return true;
  if (value === 'Not applicable' || value === '<NOT_APPLICABLE>') return false;

  return false;
}

export function isFieldExtracting(value: FieldValue | null | undefined): boolean {
  if (!value || isGroupedFieldValue(value)) return false;
  if (Array.isArray(value)) return value.length === 1 && value[0] === FIELD_VALUE_EXTRACTING;
  return value === FIELD_VALUE_EXTRACTING;
}

export function isFieldNotApplicable(value: FieldValue | null | undefined): boolean {
  if (!value || isGroupedFieldValue(value)) return false;
  if (Array.isArray(value)) {
    return value.length === 1 && (value[0] === 'Not applicable' || value[0] === '<NOT_APPLICABLE>');
  }
  return value === 'Not applicable' || value === '<NOT_APPLICABLE>';
}

/**
 * Sort options so selected values appear first, with "Not Applicable" always at the top.
 */
export function sortOptionsSelectedFirst(
  options: { value: string; label: string }[],
  selectedValues: (string | number)[]
): { value: string; label: string }[] {
  const selectedSet = new Set(selectedValues.map(v => String(v)));
  const notApplicable = options.filter(opt => opt.value === '<NOT_APPLICABLE>');
  const regular = options.filter(opt => opt.value !== '<NOT_APPLICABLE>');
  const selected = regular.filter(opt => selectedSet.has(opt.value));
  const unselected = regular.filter(opt => !selectedSet.has(opt.value));
  return [...notApplicable, ...selected, ...unselected];
}

/**
 * Format selected option labels for display in Select valueLabel.
 * Shows comma-separated labels, truncated with "+N more" if too long.
 */
export function formatSelectedLabels(
  selectedOptions: { value: string | number; label?: string }[],
  maxLength: number = 50
): string {
  if (!selectedOptions || selectedOptions.length === 0) return '';
  if (selectedOptions.some(opt => opt.value === '<NOT_APPLICABLE>')) return 'Not Applicable';
  const labels = selectedOptions.map(opt => opt.label || String(opt.value));
  const joined = labels.join(', ');
  if (joined.length <= maxLength) return joined;
  let result = '';
  let count = 0;
  for (const label of labels) {
    const candidate = count === 0 ? label : `${result}, ${label}`;
    if (candidate.length > maxLength && count > 0) {
      return `${result}, +${labels.length - count} more`;
    }
    result = candidate;
    count++;
  }
  return result.length > maxLength ? result.substring(0, maxLength - 3) + '...' : result;
}

/**
 * Get field names in order from backend config
 */
export function getFieldOrder(config: FieldsConfig | null): string[] {
  return config?.field_order || [];
}

export function getQuestionFields(config: FieldsConfig | null): string[] {
  if (!config) return [];
  return config.field_order.filter(fieldName => {
    const field = config.fields[fieldName];
    if (!field) return false;
    if (field.respondent === 'system') return false;
    if (!field.question || !field.question.trim()) return false;
    return true;
  });
}

export function getFieldsByRespondent(
  config: FieldsConfig | null,
  respondent: 'live' | 'post_session'
): string[] {
  if (!config) return [];
  return config.field_order.filter(fieldName => {
    const field = config.fields[fieldName];
    if (!field) return false;
    if (field.respondent === 'system') return false;
    // Fields without respondent are treated as 'live' (e.g., navigator_default profile)
    const effectiveRespondent = field.respondent || 'live';
    return effectiveRespondent === respondent;
  });
}

/**
 * Get field definition by name
 */
export function getFieldDefinition(config: FieldsConfig | null, fieldName: string) {
  return config?.fields[fieldName];
}

/**
 * Resolve the value-hierarchy group a field belongs to — the whole chain of fields
 * linked by depends_on where the child's options depend on the parent's value
 * (i.e. the child has a hierarchy_mapping), e.g. principal → detailed body part →
 * symptoms. Walks up to the hierarchy root, then collects all hierarchy descendants
 * (root first, in field_order). Plain conditional dependencies (depends_on_value gates
 * like omr_or_not, which have no hierarchy_mapping) are NOT followed, so unrelated
 * fields don't get grouped. A standalone field returns just itself.
 */
export function getFieldGroup(config: FieldsConfig | null, fieldName: string): string[] {
  if (!config || !config.fields[fieldName]) return [fieldName];

  // A field is a "hierarchy child" when it depends on a real parent and its own
  // options vary by that parent's value (hierarchy_mapping present).
  const isHierarchyChild = (f: string): boolean => {
    const def = config.fields[f];
    return !!(def?.depends_on && config.fields[def.depends_on] && def.hierarchy_mapping);
  };

  // Walk up to the hierarchy root.
  let root = fieldName;
  while (isHierarchyChild(root)) root = config.fields[root].depends_on as string;

  // Collect the root plus all hierarchy descendants, root first.
  const group = [root];
  const queue = [root];
  while (queue.length) {
    const node = queue.shift() as string;
    for (const f of config.field_order) {
      if (config.fields[f]?.depends_on === node && isHierarchyChild(f) && !group.includes(f)) {
        group.push(f);
        queue.push(f);
      }
    }
  }
  return group;
}

/**
 * Flatten a field value into the list of meaningful selections it contributes as a
 * parent (used to drive a dependent child's per-parent dropdowns). Handles scalar,
 * list and grouped (dict) values; drops the <UNKNOWN> sentinel.
 */
/** The value of a field's hierarchy parent, which its own options depend on. */
export function parentValueOf(
  config: FieldsConfig | null,
  fieldName: string,
  values: Partial<Record<string, FieldValue>>,
): FieldValue | undefined {
  const dependsOn = getFieldDefinition(config, fieldName)?.depends_on;
  return dependsOn ? values[dependsOn] : undefined;
}

export function toParentValues(v: FieldValue | undefined): string[] | undefined {
  if (isGroupedFieldValue(v)) {
    return ([] as string[])
      .concat(...Object.values(v).map((x) => (Array.isArray(x) ? x : [x])))
      .filter((x) => x && x !== '<UNKNOWN>');
  }
  if (Array.isArray(v)) return v.filter((x) => x && x !== '<UNKNOWN>');
  if (typeof v === 'string' && v && v !== '<UNKNOWN>') return [v];
  return undefined;
}

/**
 * Apply a field edit, re-seeding the grouped dict structure of any direct child fields
 * whose options depend on the changed field. New parent values get an empty child list;
 * deselected parents are removed. Returns a new map (does not mutate `current`).
 * Shared by the extracted-fields editor and the evidence-modal editor.
 */
export function applyGroupedChildInit(
  config: FieldsConfig | null,
  changedField: string,
  value: FieldValue,
  current: Partial<Record<string, FieldValue>>,
): Partial<Record<string, FieldValue>> {
  const updated: Partial<Record<string, FieldValue>> = { ...current, [changedField]: value };
  if (!config) return updated;

  let parentValues: string[] | undefined;
  if (Array.isArray(value)) {
    parentValues = value.filter((v) => v !== '<UNKNOWN>' && v !== '<NOT_APPLICABLE>');
  } else if (isGroupedFieldValue(value)) {
    parentValues = ([] as string[])
      .concat(...Object.values(value))
      .filter((v) => v && v !== '<UNKNOWN>' && v !== '<NOT_APPLICABLE>');
  }

  if (parentValues && parentValues.length > 0) {
    for (const childName of Object.keys(config.fields)) {
      if (config.fields[childName]?.depends_on !== changedField) continue;
      const currentChild = updated[childName];
      const grouped: Record<string, string[]> = isGroupedFieldValue(currentChild)
        ? { ...currentChild }
        : {};
      for (const pv of parentValues) if (!grouped[pv]) grouped[pv] = [];
      for (const key of Object.keys(grouped)) if (!parentValues.includes(key)) delete grouped[key];
      updated[childName] = grouped;
    }
  }
  return updated;
}

/**
 * Resolve the option list a field should offer, preferring the current site's
 * captured options over the profile defaults. Mirrors the backend get_field_options:
 *   1. site.dropdown_options[field]  (flat list, or a parent->children map)
 *   2. site.process_paths / site.locations  (for those two site-scoped fields)
 *   3. the profile field's options
 * For a per-parent site entry, returns an effective hierarchy_mapping (site parent
 * wins where present, else the profile mapping — so a partial site hierarchy is safe)
 * and a flattened option superset the renderer filters per parent.
 */
export function resolveFieldOptions(
  fieldDef: FieldDefinition | undefined,
  currentSite: SiteMetadata | null | undefined,
): { options: FieldOption[]; hierarchyMapping?: Record<string, string[]> } {
  if (!fieldDef) return { options: [] };

  const profileOptions = fieldDef.options || [];
  const hierarchyMapping = fieldDef.hierarchy_mapping;
  const labelByValue = new Map(profileOptions.map(o => [o.value, o.label]));
  const toOptions = (values: string[]): FieldOption[] => {
    const seen = new Set<string>();
    const out: FieldOption[] = [];
    for (const v of values) {
      if (!v || seen.has(v)) continue;
      seen.add(v);
      out.push({ value: v, label: labelByValue.get(v) || v });
    }
    return out;
  };

  const siteEntry = currentSite?.dropdown_options?.[fieldDef.name];

  // Per-parent (hierarchical) site options. The site map may be partial — both in
  // which parents it covers and which options it has per parent — so merge with the
  // profile mapping: the site's options come first, then any profile options for that
  // parent not already present; parents absent from the site keep the profile list.
  if (siteEntry && !Array.isArray(siteEntry)) {
    const mergedMapping: Record<string, string[]> = { ...(hierarchyMapping || {}) };
    for (const [parent, siteOpts] of Object.entries(siteEntry)) {
      if (!Array.isArray(siteOpts) || siteOpts.length === 0) continue;
      const seen = new Set(siteOpts);
      const profileOpts = hierarchyMapping?.[parent] || [];
      mergedMapping[parent] = [...siteOpts, ...profileOpts.filter(o => !seen.has(o))];
    }
    const flat = ([] as string[]).concat(...Object.values(mergedMapping));
    return { options: toOptions(flat), hierarchyMapping: mergedMapping };
  }

  // Flat site options for this field.
  if (Array.isArray(siteEntry) && siteEntry.length > 0) {
    return { options: toOptions(siteEntry), hierarchyMapping };
  }

  // Site base lists for the two site-scoped fields.
  if (currentSite) {
    if (fieldDef.name === 'process_path' && currentSite.process_paths?.length) {
      return { options: toOptions(currentSite.process_paths), hierarchyMapping };
    }
    if (fieldDef.name.includes('location') && currentSite.locations?.length) {
      return { options: toOptions(currentSite.locations), hierarchyMapping };
    }
  }

  return { options: profileOptions, hierarchyMapping };
}

/**
 * Get field label by name
 */
export function getFieldLabel(config: FieldsConfig | null, fieldName: string): string {
  const field = getFieldDefinition(config, fieldName);
  return field?.label || fieldName;
}

/**
 * Get field question by name
 */
export function getFieldQuestion(config: FieldsConfig | null, fieldName: string): string {
  const field = getFieldDefinition(config, fieldName);
  return field?.question || '';
}

/**
 * Get all field names from config
 */
export function getAllFieldNames(config: FieldsConfig | null): string[] {
  return config?.fields ? Object.keys(config.fields) : [];
}

/**
 * Get visible question fields respecting hierarchy (depends_on) rules:
 * - Parent unknown → show parent only
 * - Parent answered, child unknown → show parent (answered) + child (pending)
 * - Both answered → show parent only
 * - If depends_on_value is set, child only shows when parent equals that value
 */
export function getVisibleQuestionFields(
  config: FieldsConfig | null,
  answeredFields: Set<string>,
  respondent?: 'live' | 'post_session',
  extractedFields?: Record<string, any>,
): string[] {
  if (!config) return [];

  const baseFields = respondent
    ? getFieldsByRespondent(config, respondent).filter(f => config.fields[f]?.question?.trim())
    : getQuestionFields(config);

  return baseFields.filter(fieldName => {
    const fieldDef = config.fields[fieldName];
    if (!fieldDef?.depends_on) return true;

    // Parent must be answered
    if (!answeredFields.has(fieldDef.depends_on)) return false;
    // Child must still be pending
    if (answeredFields.has(fieldName)) return false;

    // If depends_on_value is set, check parent value matches
    if (fieldDef.depends_on_value && extractedFields) {
      const parentValue = extractedFields[fieldDef.depends_on];
      const requiredValues = Array.isArray(fieldDef.depends_on_value)
        ? fieldDef.depends_on_value.map((v: string) => v.toLowerCase())
        : [fieldDef.depends_on_value.toLowerCase()];
      if (typeof parentValue === 'string') {
        if (!requiredValues.includes(parentValue.toLowerCase())) return false;
      } else if (Array.isArray(parentValue)) {
        if (!parentValue.some(v => requiredValues.includes(v.toLowerCase()))) return false;
      } else {
        return false;
      }
    }

    return true;
  });
}

/**
 * Extract format placeholder from a field description.
 * Checks for known date/time format tokens in the description.
 */
export function getFieldFormatPlaceholder(description?: string): string | undefined {
  if (!description) return undefined;
  const upper = description.toUpperCase();
  const hasDate = upper.includes('MM/DD/YYYY');
  const hasTime = upper.includes('HH:MM AM/PM');
  if (hasDate && hasTime) return 'MM/DD/YYYY HH:MM AM/PM';
  if (hasDate) return 'MM/DD/YYYY';
  if (hasTime) return 'HH:MM AM/PM';
  return undefined;
}

function isValidDate(month: number, day: number, year: number): boolean {
  if (month < 1 || month > 12) return false;
  if (year < 1900 || year > 2099) return false;
  const daysInMonth = new Date(year, month, 0).getDate();
  return day >= 1 && day <= daysInMonth;
}

function isValidTime(hour: number, minute: number, ampm: string): boolean {
  if (hour < 1 || hour > 12) return false;
  if (minute < 0 || minute > 59) return false;
  return ampm === 'AM' || ampm === 'PM';
}

/**
 * Validate a value against a known format placeholder.
 * Returns true if the value matches the expected format, or if no format is specified.
 * Empty/unknown values are always valid (they're just not filled yet).
 */
export function validateFieldFormat(value: string, format?: string): boolean {
  if (!format || !value || value === '<UNKNOWN>') return true;

  if (format === 'MM/DD/YYYY') {
    const parts = value.split('/');
    if (parts.length !== 3) return false;
    const [mm, dd, yyyy] = parts.map(Number);
    return !isNaN(mm) && !isNaN(dd) && !isNaN(yyyy) && isValidDate(mm, dd, yyyy);
  }

  if (format === 'HH:MM AM/PM') {
    const timeParts = value.trim().split(' ');
    if (timeParts.length !== 2) return false;
    const [hhmm, ampm] = timeParts;
    const colonParts = hhmm.split(':');
    if (colonParts.length !== 2) return false;
    const [hh, mm] = colonParts.map(Number);
    return !isNaN(hh) && !isNaN(mm) && isValidTime(hh, mm, ampm.toUpperCase());
  }

  if (format === 'MM/DD/YYYY HH:MM AM/PM') {
    const spaceIdx = value.indexOf(' ');
    if (spaceIdx === -1) return false;
    const datePart = value.substring(0, spaceIdx);
    const timePart = value.substring(spaceIdx + 1);
    return validateFieldFormat(datePart, 'MM/DD/YYYY') && validateFieldFormat(timePart, 'HH:MM AM/PM');
  }

  return true;
}

/**
 * Order-insensitive deep equality check for FieldValue.
 * Arrays are compared as sets (order doesn't matter).
 * Grouped objects compare keys and recursively compare their array values.
 */
export function fieldValuesEqual(a: FieldValue | undefined, b: FieldValue | undefined): boolean {
  if (a === b) return true;
  if (!a || !b) return false;
  if (typeof a === 'string' || typeof b === 'string') return a === b;
  if (Array.isArray(a) && Array.isArray(b)) {
    if (a.length !== b.length) return false;
    const sortedA = [...a].sort();
    const sortedB = [...b].sort();
    return sortedA.every((v, i) => v === sortedB[i]);
  }
  if (!Array.isArray(a) && !Array.isArray(b)) {
    const keysA = Object.keys(a).sort();
    const keysB = Object.keys(b).sort();
    if (keysA.length !== keysB.length) return false;
    return keysA.every((k, i) => k === keysB[i] && fieldValuesEqual(a[k], b[k]));
  }
  return false;
}

/**
 * Resolve the value shown for each field by layering, lowest → highest precedence:
 *   extracted (LLM)  <  human overrides  <  submitted (final AUSTIN form values).
 * Later layers win via object spread; absent layers are skipped.
 *
 * @param extractedFields - LLM extracted fields
 * @param humanOverrides - Human override fields (win over extracted)
 * @param submittedFields - Values captured from the AUSTIN form at submit (win over both)
 */
export function mergeWithHumanOverrides(
  extractedFields: Partial<Record<string, FieldValue>>,
  humanOverrides?: Record<string, FieldValue>,
  submittedFields?: Record<string, FieldValue>
): Partial<Record<string, FieldValue>> {
  return {
    ...extractedFields,
    ...(humanOverrides || {}),
    ...(submittedFields || {}),
  };
}
